import { useCallback, useEffect, useMemo, useState } from 'react';
import './App.css';

import { ACTIVE_EVENT_STORAGE_KEY, readStoredEventId, storeEventId } from './api/activeEvent';
import { AuthContext } from './api/authContext';
import { ApiError, endSession, fetchBundles, fetchCatalog, fetchEvents } from './api/client';
import type { AuthSession, EventSummary, SalesFocus } from './api/client';
import { EventContext } from './api/eventContext';
import {
  clearSessionExpiry,
  clearSessionProfile,
  readStoredProfile,
  storeSessionExpiry,
  storeSessionProfile,
  storedTokenExpired,
  type SessionProfile,
} from './api/session';
import LandingAtmosphere from './components/auth/LandingScene';
import LoginCard from './components/auth/LoginCard';
import Cashier from './components/Cashier';
import CustomerDisplay from './components/display/CustomerDisplay';
import CatalogPage from './components/group/CatalogPage';
import ReportsPage from './components/group/ReportsPage';
import SettingsPage from './components/group/SettingsPage';
import PendingSync from './components/offline/PendingSync';
import UpdatePrompt from './components/offline/UpdatePrompt';
import { syncer, setSyncSessionReader } from './offline';
import { setQueueOwner } from './offline/queue';
import type { SyncStatus } from './offline/syncer';
import { APP_FEATURES, APP_NAME, APP_TAGLINE, GOOGLE_DATA_USE } from './shared/brand';
import { clearUser, identifyUser, setView } from './shared/faro';
import { BUNDLE_CACHE_KEY, cacheBundles, readCachedBundles } from './shared/functions/bundles';
import { Button, IconButton, Meta, Notice, Stack, StatusBadge } from './ui';
import UiShowcase from './ui/UiShowcase';

// Re-exported so existing importers keep working; the definition moved out to
// break the App <-> component import cycle.
export { AuthContext } from './api/authContext';

/** The till, the grouped editors, or the customer display. */
type View = 'cashier' | 'catalog' | 'reports' | 'settings' | 'customer' | 'ui';

/** A destination plus, optionally, the section of it to land on. */
type Route = { view: View; section: string | null };

/**
 * The route encoded in the URL hash.
 *
 * The hash (rather than a real path) keeps deep links working under every
 * deployment: the same bundle is served at "/" and under "/Cashier-App/", and a
 * path like "/reports" would need an SPA fallback that GitHub Pages does not give
 * us. The per-page hashes this app used before (#cms, #sales, #events,
 * #discounts, #payment, #users) still resolve, so old links keep working.
 */
const HASH_ROUTES: Record<string, Route> = {
  catalog: { view: 'catalog', section: null },
  'catalog-items': { view: 'catalog', section: 'items' },
  'catalog-bundles': { view: 'catalog', section: 'bundles' },
  cms: { view: 'catalog', section: 'items' },
  discounts: { view: 'catalog', section: 'bundles' },
  reports: { view: 'reports', section: null },
  'reports-sales': { view: 'reports', section: 'sales' },
  'reports-events': { view: 'reports', section: 'events' },
  sales: { view: 'reports', section: 'sales' },
  events: { view: 'reports', section: 'events' },
  settings: { view: 'settings', section: null },
  'settings-payment': { view: 'settings', section: 'payment' },
  'settings-users': { view: 'settings', section: 'users' },
  payment: { view: 'settings', section: 'payment' },
  users: { view: 'settings', section: 'users' },
  customer: { view: 'customer', section: null },
  // The internal primitive reference; not linked from the navigation.
  ui: { view: 'ui', section: null },
};

function routeFromHash(): Route {
  const raw = window.location.hash.replace(/^#\/?/, '');
  return HASH_ROUTES[raw] ?? { view: 'cashier', section: null };
}

/** What the phone's compact bar calls each view. */
const PAGE_LABELS: Record<View, string> = {
  cashier: 'Till',
  catalog: 'Catalog',
  reports: 'Reports',
  settings: 'Settings',
  customer: 'Display',
  ui: 'UI primitives',
};

/**
 * A Google redirect carries its result in the query string. A visitor returning
 * to an error must not watch the entrance celebration play out over the notice,
 * so the landing renders its instant variant instead.
 */
const OAUTH_RETURN = (() => {
  if (typeof window === 'undefined') return false;
  const query = new URLSearchParams(window.location.search);
  return query.has('google_code') || query.has('google_error');
})();

function App() {
  const [session, setSession] = useState<SessionProfile | null>(readStoredProfile);
  const [categoryItems, setCategoryItems] = useState<CategoryItem[]>([]);
  /**
   * Whether the live catalogue has answered at least once.
   *
   * Emptiness alone cannot tell "every item is hidden" from "the fetch has not
   * landed", and the till falls back to its offline copy when it cannot tell -
   * which would put a hidden item back on screen.
   */
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  /**
   * True once the catalogue fetch has failed. The till needs this to tell
   * "still loading" from "could not load": both leave an empty menu, and an
   * empty menu is otherwise indistinguishable from an empty catalogue.
   */
  const [catalogFailed, setCatalogFailed] = useState(false);
  // Start from the cache so an offline reload can still apply bundle discounts.
  const [bundles, setBundles] = useState<CatalogBundle[]>(readCachedBundles);
  const [catalogError, setCatalogError] = useState('');
  const [route, setRoute] = useState<Route>(routeFromHash);
  const view = route.view;
  /** The phone menu. The desktop nav lays the same controls out inline. */
  const [menuOpen, setMenuOpen] = useState(false);
  // Bumped to force the catalog effect to re-run after CMS edits.
  const [catalogRevision, setCatalogRevision] = useState(0);
  /**
   * Booth events the till can file sales under.
   *
   * Only the chosen id is persisted; the list stays authoritative, so an event
   * renamed or archived elsewhere cannot leave a stale label on this device.
   */
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [activeEventId, setActiveEventId] = useState<number | null>(readStoredEventId);
  const [eventsRevision, setEventsRevision] = useState(0);
  /** Which event the sales page should open on, when sent there from events. */
  const [salesFocus, setSalesFocus] = useState<SalesFocus | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    online: false,
    pending: 0,
    syncing: false,
    lastError: null,
    conflicts: 0,
    lastOrderRef: null,
  });
  const [showPending, setShowPending] = useState(false);
  const [, forceTick] = useState(0);

  /** Switch view and record it in the URL so a refresh stays put. */
  const navigate = useCallback((next: View, section?: string) => {
    // So an error or a slow screen arrives with the view it happened on.
    setView(next);
    // Any navigation closes the phone menu; nothing should sit behind it.
    setMenuOpen(false);
    const hash = section ? '#' + next + '-' + section : '#' + next;
    if (window.location.hash === hash) {
      setRoute({ view: next, section: section ?? null });
      return;
    }
    // pushState rather than assigning the hash: assigning it makes the browser
    // scroll the fragment into view, and its ancestors include the document, so
    // the whole shell moved and the nav bar left the screen. pushState updates
    // the URL and the back button without any scrolling; the state is set here
    // instead of waiting for a hashchange that pushState does not fire.
    window.history.pushState(null, '', hash);
    setRoute({ view: next, section: section ?? null });
  }, []);

  useEffect(() => {
    const sync = () => {
      setRoute(routeFromHash());
      setMenuOpen(false);
    };
    window.addEventListener('hashchange', sync);
    return () => window.removeEventListener('hashchange', sync);
  }, []);

  /*
   * The phone drawer is a disclosure, not a dialog: it closes on Escape or a
   * pointer outside it, and the view it covers is inert while it is open so a
   * tap cannot land on the content underneath. The toggle is excluded from the
   * outside check because its own click is what toggles the drawer.
   */
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (target?.closest('#appNavMenu') || target?.closest('.appNavToggle')) return;
      setMenuOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [menuOpen]);

  // The syncer only needs to know whether the session is still live; the
  // credential itself rides the HttpOnly cookie and is never read here. An
  // expired session is withheld so it stops trying to sync (the server would
  // 401), while the till keeps working.
  useEffect(() => {
    setSyncSessionReader(() => session !== null && !storedTokenExpired());
    // The queue is per account, so B never syncs A's unsynced sales.
    setQueueOwner(session?.username ?? null);
    const unsubscribe = syncer.subscribe(setSyncStatus);
    syncer.start();
    return () => {
      unsubscribe();
      syncer.stop();
    };
  }, [session]);

  const logout = useCallback(() => {
    // Only the server can clear an HttpOnly cookie, so ask it to as well. A
    // failure must not strand the operator on a screen they asked to leave.
    void endSession().catch(() => {});
    clearSessionProfile();
    clearSessionExpiry();
    // Drop this account's cached catalogue, bundles and event choice so the
    // next person to sign in on this device cannot see them offline. The cart
    // and sales queue are already keyed per account and are left in place.
    localStorage.removeItem('cachedData');
    localStorage.removeItem(BUNDLE_CACHE_KEY);
    localStorage.removeItem(ACTIVE_EVENT_STORAGE_KEY);
    setSession(null);
    // Telemetry must not keep attributing events to the account that left.
    clearUser();
    setCategoryItems([]);
    setCatalogLoaded(false);
    setBundles([]);
    setCatalogError('');
    setEvents([]);
    navigate('cashier');
  }, [navigate]);

  const authenticate = useCallback((next: AuthSession) => {
    const profile: SessionProfile = { username: next.username, is_admin: next.is_admin };
    storeSessionProfile(profile);
    // The server said how long the cookie lives; record it so the offline
    // session check never has to read a credential it cannot see.
    storeSessionExpiry(Date.now() + next.expires_in * 1000);
    setSession(profile);
    // This device is shared, so attribution is set per sign-in, not per browser.
    identifyUser(profile.username);
    // A fresh session is the moment to reconcile anything queued offline.
    syncer.poke();
  }, []);

  // Load the catalog whenever we have a session. Runs once per login rather than
  // once per render, and every state update happens in the promise callbacks.
  useEffect(() => {
    if (!session) {
      return;
    }
    if (storedTokenExpired()) {
      // Offline with a stale session: skip the doomed request. The till renders
      // from the cached catalogue and queued sales sync after a re-login.
      return;
    }

    let cancelled = false;

    // Bundles are priced like catalog items, so they travel with the menu and
    // are cached alongside it. A failure is handled exactly as before.
    Promise.all([fetchCatalog(), fetchBundles()])
      .then(([items, nextBundles]) => {
        if (cancelled) return;
        setCategoryItems(items);
        setCatalogLoaded(true);
        setCatalogFailed(false);
        setBundles(nextBundles);
        // Persist for the offline till; an empty list is cached too, so
        // deleting every bundle offline stops applying the old ones.
        cacheBundles(nextBundles);
        setCatalogError('');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiError && error.status === 401) {
          logout();
          return;
        }
        // Unreachable backend is expected offline. The till falls back to the
        // cached catalogue, so an error banner would be noise - but with no
        // cache there is nothing to sell from, and the till has to say so.
        if (error instanceof ApiError && error.status === 0) {
          setCatalogFailed(true);
          return;
        }
        setCatalogFailed(true);
        setCatalogError(error instanceof Error ? error.message : 'Failed to load the catalog.');
      });

    return () => {
      cancelled = true;
    };
  }, [session, logout, catalogRevision]);

  // The event list feeds the nav picker, the record dialog and the report's
  // scope selector. It is refetched on every view change so a booth created
  // elsewhere (or through the API) is selectable without a reload. An
  // unreachable backend is expected offline: a failure leaves the list as it was
  // and the till keeps filing under the event it already had.
  useEffect(() => {
    if (!session || storedTokenExpired()) return;

    let cancelled = false;

    fetchEvents()
      .then((overview) => {
        if (cancelled) return;
        setEvents(overview.events);
        // Reconcile the remembered choice with the list that actually exists, in
        // the callback that delivers it. Doing this in its own effect would set
        // state synchronously and cascade a second render for nothing. A stored
        // id that is gone (deleted or archived on another device) clears; with
        // exactly one open event and nothing stored there is no decision to
        // make, so pick it — which is what makes a seeded till usable at once.
        setActiveEventId((current) => {
          if (current !== null && overview.events.some((event) => event.id === current)) return current;
          return current === null && overview.events.length === 1 ? overview.events[0].id : null;
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiError && error.status === 401) {
          logout();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, logout, eventsRevision, view]);

  // Persist the choice. An effect that only mirrors state into an external store
  // is the sanctioned shape: it never sets state, so it cannot cascade a render.
  useEffect(() => {
    storeEventId(activeEventId);
  }, [activeEventId]);

  const activeEvent = useMemo(
    () => (activeEventId === null ? null : (events.find((event) => event.id === activeEventId) ?? null)),
    [events, activeEventId]
  );

  const chooseActiveEvent = useCallback((event: EventSummary | null) => {
    // Persisted by the effect above, so there is one writer for the stored value.
    setActiveEventId(event ? event.id : null);
  }, []);

  // Re-render periodically so the "session expired" prompt appears on its own
  // rather than waiting for the next user interaction.
  useEffect(() => {
    const timer = setInterval(() => forceTick((tick) => tick + 1), 60_000);
    return () => clearInterval(timer);
  }, []);

  /** Leaving the CMS: pick up whatever the editor just changed. */
  const showCashier = useCallback(() => {
    navigate('cashier');
    setCatalogRevision((revision) => revision + 1);
  }, [navigate]);

  /** Open the sales report, optionally focused on one event (or Unassigned). */
  const showSales = useCallback(
    (focus: SalesFocus | null) => {
      setSalesFocus(focus);
      navigate('reports', 'sales');
    },
    [navigate]
  );

  // Recomputed whenever the session changes or a sync tick lands, so an expired
  // session is noticed without waiting for the next interaction.
  const sessionStale = session !== null && storedTokenExpired();
  const offline = !syncStatus.online;

  const statusLabel = offline
    ? `offline${syncStatus.pending > 0 ? ` · ${syncStatus.pending} queued` : ''}`
    : sessionStale && syncStatus.pending > 0
      ? 'sign in to sync'
      : syncStatus.syncing
        ? 'syncing…'
        : syncStatus.pending > 0
          ? `${syncStatus.pending} queued`
          : 'online';

  // The pill's tone: connected is success, a queue is a warning, and no
  // connection is the loud one. The label above still carries the detail.
  const statusBadgeTone = offline ? 'danger' : syncStatus.pending > 0 ? 'warning' : 'success';

  return (
    <AuthContext.Provider value={{ username: session?.username ?? null, isAdmin: session?.is_admin === true, logout }}>
      <EventContext.Provider value={{ events, activeEvent, setActiveEvent: chooseActiveEvent }}>
        <UpdatePrompt />
        {catalogError && (
          <Notice variant="error" className="appError">
            {catalogError}
          </Notice>
        )}
        {session && view === 'customer' ? (
          <CustomerDisplay onExit={() => navigate('cashier')} />
        ) : session ? (
          <div className="appShell">
            <nav className={menuOpen ? 'appNav appNavOpen' : 'appNav'}>
              {/* Phones get a compact bar with a menu; the desktop nav lays the
                  same controls out inline and ignores the toggle. */}
              <IconButton
                label="Menu"
                className="appNavToggle"
                aria-expanded={menuOpen}
                aria-controls="appNavMenu"
                onClick={() => setMenuOpen((open) => !open)}
              >
                <span aria-hidden="true">☰</span>
              </IconButton>
              <span className="appNavTitle">{PAGE_LABELS[view]}</span>
              <div className="appNavMenu" id="appNavMenu">
                {/* Primary views, then the booth context, then device status. */}
                <Stack direction="row" gap={1} className="appNavViews">
                  <Button variant="ghost" className={view === 'cashier' ? 'appNavActive' : undefined} onClick={showCashier}>
                    till
                  </Button>
                  <Button
                    variant="ghost"
                    className={view === 'catalog' ? 'appNavActive' : undefined}
                    onClick={() => navigate('catalog')}
                    disabled={offline}
                    title={offline ? 'The catalogue editor needs a connection.' : undefined}
                  >
                    catalog
                  </Button>
                  <Button
                    variant="ghost"
                    className={view === 'reports' ? 'appNavActive' : undefined}
                    onClick={() => navigate('reports')}
                    disabled={offline}
                    title={offline ? 'Sales and booth events need a connection.' : undefined}
                  >
                    reports
                  </Button>
                  <Button
                    variant="ghost"
                    className={view === 'settings' ? 'appNavActive' : undefined}
                    onClick={() => navigate('settings')}
                    disabled={offline}
                    title={offline ? 'Payment and account settings need a connection.' : undefined}
                  >
                    settings
                  </Button>
                  {/* Open the customer-facing mirror of the till. Set this up on the
                  second screen or iPad; it needs no other configuration. */}
                  <Button
                    variant="ghost"
                    className="appNavUtility"
                    onClick={() => navigate('customer')}
                    title="Show the customer what they are buying — open this on the customer's screen"
                  >
                    display
                  </Button>
                </Stack>
                {/* The booth new sales are filed under. Set once at the start of an
                  event and left alone; the record dialog can still override it. */}
                <label className="appEventPicker">
                  <Meta as="span" className="appEventPickerLabel">
                    event
                  </Meta>
                  <select
                    value={activeEventId ?? ''}
                    onChange={(changed) => {
                      const next = events.find((entry) => entry.id === Number(changed.target.value)) ?? null;
                      chooseActiveEvent(next);
                    }}
                    disabled={events.length === 0}
                    title="The booth event new sales are filed under"
                  >
                    <option value="">no event</option>
                    {events.map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.name}
                      </option>
                    ))}
                  </select>
                </label>
                <Meta as="span" className="appNavUser">
                  {session?.username}
                </Meta>
                <Button variant="ghost" className="appSignOut" onClick={logout} title="Sign out of this account">
                  sign out
                </Button>
              </div>
              {/* Status stays in the compact phone bar. On a desktop the order
                  rules put it back between the event picker and the account. */}
              <div className="appNavStatus">
                <button type="button" className="appSyncChip" onClick={() => setShowPending(true)} title="Sales saved on this device">
                  <StatusBadge tone={statusBadgeTone}>{statusLabel}</StatusBadge>
                </button>
              </div>
            </nav>
            {/* Takes the height left over by the nav. The till sizes itself from
                its parent, so giving the nav its own flow height used to push the
                reset/change row off the bottom of the viewport. */}
            {/* Inert while the phone drawer is open: nothing behind it should
                take a tap or a tab stop. Desktop never opens it. */}
            <div className="appView" inert={menuOpen}>
              {view === 'catalog' && (
                <CatalogPage
                  section={route.section}
                  onSectionChange={(next) => navigate('catalog', next)}
                  onExit={showCashier}
                  onUnauthorized={logout}
                />
              )}
              {view === 'reports' && (
                <ReportsPage
                  section={route.section}
                  onSectionChange={(next) => navigate('reports', next)}
                  focus={salesFocus}
                  onOpenEvents={() => navigate('reports', 'events')}
                  onOpenSales={showSales}
                  onEventsChanged={() => setEventsRevision((revision) => revision + 1)}
                  onExit={showCashier}
                  onUnauthorized={logout}
                />
              )}
              {view === 'settings' && (
                <SettingsPage
                  section={route.section}
                  onSectionChange={(next) => navigate('settings', next)}
                  currentUsername={session?.username ?? null}
                  isAdmin={session?.is_admin === true}
                  onExit={showCashier}
                  onUnauthorized={logout}
                />
              )}
              {view === 'cashier' && (
                <Cashier categoryItems={categoryItems} bundles={bundles} catalogLoaded={catalogLoaded} catalogFailed={catalogFailed} />
              )}
              {view === 'ui' && <UiShowcase />}
            </div>
            {showPending && <PendingSync status={syncStatus} onClose={() => setShowPending(false)} />}
          </div>
        ) : (
          <div className={OAUTH_RETURN ? 'authPage authPage--instant' : 'authPage'}>
            {/* The root URL is the app home page a reviewer inspects, so it has
                to say what the app does before it asks anyone to sign in. */}
            <LandingAtmosphere />
            <section className="authPurpose">
              <h1>{APP_NAME}</h1>
              <p>{APP_TAGLINE}</p>
              <p>{APP_FEATURES}</p>
              <p>{GOOGLE_DATA_USE}</p>
            </section>
            <LoginCard onAuthenticated={authenticate} />
          </div>
        )}
      </EventContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;
